import type { ListUserTeamMembersFn } from '@/features/iam'
import { task } from 'mobx-task'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { action, computed } from 'mobx'
import {
  type StaticColumnDetails,
  type MutableColumnDetails,
} from '@/features/jobs/list/jobs-table-columns'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { type JobSearchViewModelFactory } from '@/features/jobs/list/JobSearchViewModel'
import { type SearchableJobStore } from '@/features/jobs/list/SearchableJobStore'
import { type FilterStorageFactory } from '@/infra/FiltersViewModel'
import { type JobTicketStore } from '@/features/jobs/tickets/JobTicketStore'
import { EMPTY, filter, map, switchMap } from 'rxjs'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { tapAction, toStream } from '@/utils/mobx-util'
import { type TableSortOrder } from '@/components/Table'
import { links } from '@/utils/link'
import { Id } from '@/infra/id'
import { type SelectFilter } from '@/infra/SelectFilter'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { type JobStatusFilterOption } from '@/features/jobs/list/job-status-filter-option'
import { type FirmDashboardContractViewModel } from '@/features/jobs/dashboard/FirmDashboardContractViewModel'
import { FirmDashboardJobTableColumnsViewModel } from '@/features/jobs/dashboard/FirmDashboardJobTableColumnsViewModel'

/**
 * View model for managing the job's table within the firm dashboard.
 */
export class FirmDashboardJobTableViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Job Searcher.
   */
  readonly searcher

  /**
   * The table's columns and their CSV export.
   */
  readonly columnsVm

  /**
   * When a job is created, add it to the top of the list. The job searcher
   * does not process the JobCreated event when filters are applied.
   */
  private readonly _prependCreatedJobs$ = toStream(
    () => this.fetch.resolved && this.canEditDraftJobAsAdmin,
    true,
  ).pipe(
    switchMap((enabled) =>
      enabled
        ? this._searchableJobStore.jobEvents$.pipe(
            filter((event) => event.type === 'JobCreated'),
          )
        : EMPTY,
    ),
    tapAction((event) => {
      // We don't want to add the job to the list if it's already there or
      // if the user is searching for something or
      // if the job's workspace is not the one selected in the workspace filter.
      if (
        event.job.workspace.id !== this.currentWorkspace.id ||
        this.searcher.scroller.items.includes(event.job) ||
        this.searcher.searchText.value !== ''
      ) {
        return
      }
      this.searcher.scroller.prepend(event.job)
    }),
  )

  constructor(
    private readonly _searchableJobStore: SearchableJobStore,
    newAlert: AlertFactory,
    createAlertErrorHandler: AlertErrorHandlerFactory,
    createJobSearchViewModel: JobSearchViewModelFactory,
    private readonly _listUserTeamMembers: ListUserTeamMembersFn,
    private readonly _jobTicketStore: JobTicketStore,
    createFilterStorage: FilterStorageFactory,
    private readonly _canEditDraftJobAsAdmin: () => boolean,
    private readonly _getWorkspace: () => Workspace,
    private readonly _contractVm: FirmDashboardContractViewModel,
  ) {
    this.searcher = createJobSearchViewModel({
      filterStorage: createFilterStorage('FirmDashboardJobsTable'),
    })

    this.columnsVm = new FirmDashboardJobTableColumnsViewModel(
      newAlert,
      createAlertErrorHandler,
      () => this.searcher.getAllFilteredJobs(),
    )

    this._lifecycle
      .manage(this._prependCreatedJobs$)
      .manage(
        // Loading saved filters clears the workspace filter, so it is
        // restored whenever it drifts from the selection.
        toStream(() => this._getWorkspace(), true).pipe(
          filter((workspace) => Id.some(workspace.id)),
          switchMap((workspace) =>
            toStream(() => this.workspaceFilter.value[0], true).pipe(
              map(() => workspace),
            ),
          ),
          tapAction((workspace) => this._applyWorkspace(workspace)),
        ),
      )
      .manage(this.searcher)
  }

  /**
   * Whether the user can edit draft job as admin.
   */
  @computed
  get canEditDraftJobAsAdmin() {
    return this._canEditDraftJobAsAdmin()
  }

  /**
   * Jobs returned by the searcher.
   */
  @computed
  get filteredJobs() {
    return this.searcher.jobs
  }

  /**
   * Gets the workspace filter.
   */
  @computed
  get workspaceFilter() {
    return this.searcher.filters.getFilter('workspaces') as SelectFilter<
      string,
      Id
    >
  }

  /**
   * Gets the job status filter.
   */
  @computed
  get statusFilter() {
    return this.searcher.filters.getFilter('jobStatus') as SelectFilter<
      string,
      JobStatusFilterOption
    > | null
  }

  /**
   * Whether we are showing the empty job view
   */
  @computed
  get showEmptyView() {
    return (
      this.filteredJobs.length === 0 &&
      !this.searcher.scroller.fetch.pending &&
      this.searcher.scroller.isDone
    )
  }

  /**
   * The workspace the table lists jobs of.
   */
  @computed
  get currentWorkspace() {
    return this._getWorkspace()
  }

  /**
   * Gets the sort order for the specified column.
   */
  getSortOrder(
    column: StaticColumnDetails & MutableColumnDetails,
  ): TableSortOrder {
    return this.searcher.sorting.type === 'Field' &&
      this.searcher.sorting.field === column.field
      ? this.searcher.sorting.direction
      : 'none'
  }

  /**
   * Sets the sort order for the specified column.
   */
  setSortOrder(
    column: StaticColumnDetails & MutableColumnDetails,
    direction: TableSortOrder,
  ) {
    if (!column.field) {
      return
    }

    this.searcher.setSortField(column.field)
    this.searcher.setSortDirection(direction)
  }

  /**
   * Link to the job's details.
   */
  getJobLink(job: SearchableJob) {
    return links.job(job.id)
  }

  /**
   * Gets the latest open ticket for the specified job.
   */
  getLatestOpenTicketForJob(job: SearchableJob) {
    return this._jobTicketStore.getLatestTicketForJob(job.id)
  }

  /** @inheritDoc */
  activate() {
    this._prepareSearcher()
    this._lifecycle.activate()
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Loads the team members the job list needs.
   */
  fetch = task(async (signal?: AbortSignal) => {
    await this._listUserTeamMembers(undefined, signal)
  })

  /**
   * Narrows the searcher's filters to the ones the dashboard offers. The
   * searcher loads its saved filters on activation, so this runs before.
   */
  private _prepareSearcher() {
    this.searcher.filters.makeFilterImplicit('jobStatus')

    this.searcher.filters.removeFilter('jobScopeUpdates')
    this.searcher.filters.removeFilter('claimedRange')
    this.searcher.filters.removeFilter('closedRange')
    this.searcher.filters.removeFilter('providerCutAmountRange')
    this.searcher.filters.removeFilter('createdRange')
    this.searcher.filters.removeFilter('serviceItems')
    this.searcher.filters.removeFilter('documentTypes')
  }

  /**
   * Filters the jobs to the workspace and its contract period.
   */
  @action.bound
  private _applyWorkspace(workspace: Workspace) {
    if (this.workspaceFilter.value[0] !== workspace.id) {
      this.workspaceFilter.change([workspace.id])
    }
    this.searcher.setForcedCreatedDateRange({
      from: this._contractVm.contractStartDate,
      to: this._contractVm.contractEndDate,
    })
  }
}
